/**
 * Portfolio model (docs/portfolio-ux.md) — pure, side-effect-free, unit-tested in
 * portfolio.test.ts. Turns `GET /auth/orders` rows into what the Portfolio screen shows:
 *
 *   - open positions grouped by game, then by side + venue (contracts, average price, cost)
 *   - "Value now (est.)": contracts × the side's current best price, with a partial state while
 *     prices load
 *   - the opposite side of a two-way market and the hedge stake that locks in the same payout
 *   - the last 7 days of settled results
 *
 * Order rows carry no contract count, no average price and no position_hash (see FRICTION.md),
 * so all three are derived here: contracts = filled stake ÷ price (a whole number on Kalshi's
 * whole-contract fills, where `potential_payout` comes back net of the fee — so "Could win" uses
 * the API's `potential_payout`, and contracts don't), and the position is the `liquidity_hash`
 * minus its leading `<partner_id>:` segment — the layout the streaming docs' `ticks` example
 * shows.
 */
import type { PartnerLike, PositionLike } from "./books.js";
import { normalizeSpreadLine, totalLine } from "./books.js";
import { bestQuote, bucketOf, isGame, startMs, type ContestLike, type LiveSignal } from "./marketsBoard.js";

/* --------------------------------------------------------------------- orders */

/** The subset of a `GET /auth/orders` row the portfolio reads. */
export interface OrderLike {
  router_order_id: string;
  partner_id?: string | null;
  partner?: { partner_id?: string; partner_name?: string | null; logo_url?: string | null; internal_book?: boolean } | null;
  currency?: string | null;
  contest_id?: string | null;
  market_id?: string | null;
  market_side_id?: string | null;
  title?: string | null;
  side?: string | null;
  amount?: number | null;
  price?: number | null;
  potential_payout?: number | null;
  status?: string | null;
  order_type?: string | null;
  fill_status?: string | null;
  filled_amt?: number | null;
  unfilled_amt?: number | null;
  liquidity_hash?: string | null;
  result?: string | null;
  winnings?: number | null;
  net_winnings?: number | null;
  placed_datetime?: string | null;
  settled_datetime?: string | null;
  created_at?: string | null;
}

export type Ccy = "USD" | "ATLAS";

/** The venue-agnostic position behind an order: its liquidity_hash minus the venue segment. */
export function positionHashOf(o: OrderLike): string | null {
  const lh = o.liquidity_hash;
  if (!lh) return null;
  const pid = o.partner_id ?? o.partner?.partner_id;
  if (pid && lh.startsWith(`${pid}:`)) return lh.slice(pid.length + 1);
  const i = lh.indexOf(":");
  return i > 0 ? lh.slice(i + 1) : null;
}

/**
 * What an order actually holds: the filled stake, the contracts it bought (stake ÷ price; each
 * pays 1 if the side wins) and its potential payout as the API reports it (scaled to the filled
 * part). With no price, the payout stands in for contracts. Nothing filled → zeros (e.g. a
 * cancelled-at-venue row still `open`).
 */
export function orderFill(o: OrderLike): { stake: number; contracts: number; payout: number } {
  const none = { stake: 0, contracts: 0, payout: 0 };
  const amount = o.amount ?? 0;
  const fs = (o.fill_status ?? "").toLowerCase();
  const stake = o.filled_amt != null ? o.filled_amt : fs === "filled" ? amount : 0;
  if (!(stake > 0)) return none;
  const price = o.price ?? 0;
  const share = amount > 0 ? Math.min(1, stake / amount) : 1;
  const apiPayout = (o.potential_payout ?? 0) > 0 ? (o.potential_payout as number) * share : null;
  const contracts = price > 0 ? stake / price : (apiPayout ?? 0);
  if (!(contracts > 0)) return none;
  return { stake, contracts, payout: apiPayout ?? contracts };
}

/**
 * A short outcome label from an order title: "Milwaukee Brewers to win Full Game" →
 * "Milwaukee Brewers ML", "Under +241.5 Full Game" → "Under 241.5".
 */
export function shortOutcome(title: string | null | undefined): string {
  if (!title) return "—";
  let t = title.trim().replace(/\s+Full Game$/i, "");
  t = t.replace(/\s+to win$/i, " ML");
  t = t.replace(/\b(over|under)\s+\+/gi, "$1 ");
  return t;
}

/* ------------------------------------------------------------------ holdings */

/** One side + venue held in a game. */
export interface HoldingRow {
  /** `${positionHash}|${partnerId}` — stable across refetches. */
  key: string;
  contestId: string;
  positionHash: string | null;
  partnerId: string;
  venueName: string;
  venueLogo: string | null;
  /** The OpenMarkets practice book (every paper fill lands here, whichever venue was named). */
  internalBook: boolean;
  title: string;
  label: string;
  side: string | null;
  orders: OrderLike[];
  /** Filled stake (cost basis). */
  stake: number;
  /** Contracts held (each pays 1 if this side wins). */
  contracts: number;
  /** Potential payout as the API reports it (Could win). */
  payout: number;
  /** stake ÷ contracts. */
  avgPrice: number;
  lastPlaced: number;
  /** The play (an opportunity with 2+ legs, lib/plays.ts) these orders belong to, or null. */
  playId: string | null;
}

export interface GameHolding {
  contestId: string;
  rows: HoldingRow[];
  stake: number;
  /** Could win: Σ potential payout. */
  payout: number;
}

export const NO_CONTEST = "(no contest)";

const tsOf = (iso: string | null | undefined): number => {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(t) ? t : 0;
};

/**
 * Group open orders by game, then by side + venue. Orders with nothing filled are skipped. With
 * `playOf`, an order in a play (lib/plays.ts) gets its own row per play, so a play's legs never
 * merge with a plain bet on the same side and venue.
 */
export function buildHoldings(orders: OrderLike[], playOf: (o: OrderLike) => string | null = () => null): GameHolding[] {
  const games = new Map<string, GameHolding>();
  const rows = new Map<string, HoldingRow>();
  for (const o of orders) {
    const { stake, contracts, payout } = orderFill(o);
    if (contracts <= 0) continue;
    const contestId = o.contest_id ?? NO_CONTEST;
    const partnerId = o.partner_id ?? o.partner?.partner_id ?? "(venue)";
    const hash = positionHashOf(o);
    const playId = playOf(o);
    const key = `${hash ?? o.market_side_id ?? o.title ?? o.router_order_id}|${partnerId}${playId ? `|play:${playId}` : ""}`;
    let g = games.get(contestId);
    if (!g) games.set(contestId, (g = { contestId, rows: [], stake: 0, payout: 0 }));
    let r = rows.get(`${contestId}::${key}`);
    if (!r) {
      r = {
        key,
        contestId,
        positionHash: hash,
        partnerId,
        venueName: o.partner?.partner_name ?? partnerId,
        venueLogo: o.partner?.logo_url ?? null,
        internalBook: o.partner?.internal_book === true,
        title: o.title ?? "—",
        label: shortOutcome(o.title),
        side: o.side ?? null,
        orders: [],
        stake: 0,
        contracts: 0,
        payout: 0,
        avgPrice: 0,
        lastPlaced: 0,
        playId,
      };
      rows.set(`${contestId}::${key}`, r);
      g.rows.push(r);
    }
    r.orders.push(o);
    r.stake += stake;
    r.contracts += contracts;
    r.payout += payout;
    r.avgPrice = r.stake / r.contracts;
    r.lastPlaced = Math.max(r.lastPlaced, tsOf(o.placed_datetime ?? o.created_at));
    g.stake += stake;
    g.payout += payout;
  }
  for (const g of games.values()) {
    g.rows.sort((a, b) => b.stake - a.stake || a.key.localeCompare(b.key));
    for (const r of g.rows) r.orders.sort((a, b) => tsOf(b.placed_datetime ?? b.created_at) - tsOf(a.placed_datetime ?? a.created_at));
  }
  return [...games.values()];
}

/* ------------------------------------------------------------------- sorting */

/** Live-ness from a contest-state entry (same reading as the Markets board). */
export function liveFromState(st: { live?: boolean; state?: { game_status?: string | null } | null } | undefined): LiveSignal {
  if (!st) return null;
  if (st.live === true) return true;
  const gs = (st.state?.game_status ?? "").toLowerCase();
  if (!gs) return st.live === false ? false : null;
  return !gs.includes("pre") && !gs.includes("scheduled") && !gs.includes("final") && !gs.includes("complete");
}

/** Is this game live now (state says so, or — with no state to read — it started < 4 h ago)? */
export function isLiveGame(c: ContestLike | undefined, live: LiveSignal, now: number): boolean {
  return !!c && isGame(c) && bucketOf(c, live, now) === "live";
}

/**
 * Game cards order: live games first, then by start time (soonest first). Futures / outrights
 * (start dates are season openers) come after the games, alphabetically; contests whose
 * details haven't loaded yet sort last until they do.
 */
export function sortGames<G extends { contestId: string }>(
  games: G[],
  contestOf: (id: string) => ContestLike | undefined,
  liveOf: (id: string) => LiveSignal,
  now: number,
): G[] {
  const rank = (g: G): number => {
    const c = contestOf(g.contestId);
    if (!c) return 3;
    if (isLiveGame(c, liveOf(g.contestId), now)) return 0;
    return isGame(c) ? 1 : 2;
  };
  const start = (g: G) => {
    const c = contestOf(g.contestId);
    const s = c ? startMs(c) : NaN;
    return Number.isFinite(s) ? s : Infinity;
  };
  return [...games].sort((a, b) => {
    const ra = rank(a);
    const rb = rank(b);
    if (ra !== rb) return ra - rb;
    if (ra === 2) return (contestOf(a.contestId)?.title ?? "").localeCompare(contestOf(b.contestId)?.title ?? "");
    return start(a) - start(b) || a.contestId.localeCompare(b.contestId);
  });
}

/* --------------------------------------------------------------------- value */

/** A held side's current price: not loaded yet, nothing quotable, or the best in-scope quote. */
export type RowPrice =
  | { state: "pending" }
  | { state: "none" }
  | { state: "priced"; price: number; quote: PartnerLike; position: PositionLike };

/**
 * The held side's current best price — the lowest actionable quote among `allowed` venues
 * (Real: your connected venues; Paper: every venue), the same rule as the Markets board.
 */
export function rowPrice(
  row: Pick<HoldingRow, "positionHash">,
  data: { positions: PositionLike[] } | undefined,
  allowed: ReadonlySet<string> | null,
): RowPrice {
  if (!data) return { state: "pending" };
  const pos = row.positionHash ? data.positions.find((p) => p.position_hash === row.positionHash) : undefined;
  if (!pos) return { state: "none" };
  const q = bestQuote(pos, allowed);
  return q && typeof q.price === "number" ? { state: "priced", price: q.price, quote: q, position: pos } : { state: "none" };
}

/** Value now over a set of rows. `value`/`cost` cover the priced rows only. */
export interface ValueSummary {
  value: number;
  cost: number;
  priced: number;
  total: number;
  /** Rows whose prices haven't loaded yet. */
  pending: number;
}

export function rowsValue(rows: HoldingRow[], priceOf: (r: HoldingRow) => RowPrice): ValueSummary {
  const v: ValueSummary = { value: 0, cost: 0, priced: 0, total: rows.length, pending: 0 };
  for (const r of rows) {
    const p = priceOf(r);
    if (p.state === "pending") v.pending++;
    else if (p.state === "priced") {
      v.priced++;
      v.value += r.contracts * p.price;
      v.cost += r.stake;
    }
  }
  return v;
}

/** A game whose prices haven't been requested yet: every row pending. */
export const pendingValue = (rows: number): ValueSummary => ({ value: 0, cost: 0, priced: 0, total: rows, pending: rows });

export interface ValueTotal extends ValueSummary {
  /** value − cost over the priced rows (▲ / ▼). */
  change: number;
  /** Some row is unpriced (loading, or no quote) — the total covers only part of the book. */
  partial: boolean;
}

export function combineValues(parts: ValueSummary[]): ValueTotal {
  const t = parts.reduce<ValueSummary>(
    (a, p) => ({
      value: a.value + p.value,
      cost: a.cost + p.cost,
      priced: a.priced + p.priced,
      total: a.total + p.total,
      pending: a.pending + p.pending,
    }),
    { value: 0, cost: 0, priced: 0, total: 0, pending: 0 },
  );
  return { ...t, change: t.value - t.cost, partial: t.priced < t.total };
}

/* ---------------------------------------------------------------- opposite side */

const COMPLEMENT: Record<string, string> = { home: "away", away: "home", over: "under", under: "over", yes: "no", no: "yes" };
const norm = (s: string | null | undefined) => (s ?? "").trim().toLowerCase();

/** The line both sides of one book share: home-perspective spread, raw total, 0 for none. */
function bookLine(p: PositionLike): number {
  const side = norm(p.side_key);
  const v = side === "home" || side === "away" ? normalizeSpreadLine(p.variable, side) : totalLine(p.variable);
  return v ?? 0;
}

/** Every position that is the other side of `held`'s book (duplicates included). */
export function complementCandidates(positions: PositionLike[], held: PositionLike): PositionLike[] {
  const side = norm(held.side_key);
  const comp = COMPLEMENT[side];
  if (!comp) return [];
  const line = bookLine(held);
  const teamSides = side === "home" || side === "away";
  return positions.filter(
    (p) =>
      p !== held &&
      p.market_key === held.market_key &&
      norm(p.side_key) === comp &&
      bookLine(p) === line &&
      (teamSides || (p.participant_id ?? null) === (held.participant_id ?? null)),
  );
}

/**
 * The opposite side of a two-way market (home↔away at the mirrored spread, over↔under at the
 * same total, yes↔no on the same participant) with its best in-scope quote. Null when the market
 * has no opposite side (a futures "yes" with no "no", a multi-way market).
 */
export function findComplement(
  positions: PositionLike[],
  heldHash: string | null,
  allowed: ReadonlySet<string> | null,
): { position: PositionLike; best: PartnerLike | null } | null {
  if (!heldHash) return null;
  const held = positions.find((p) => p.position_hash === heldHash);
  if (!held) return null;
  const cands = complementCandidates(positions, held);
  if (cands.length === 0) return null;
  let pick: { position: PositionLike; best: PartnerLike | null } = { position: cands[0], best: null };
  for (const c of cands) {
    const best = bestQuote(c, allowed);
    if (best && (!pick.best || (best.price as number) < (pick.best.price as number))) pick = { position: c, best };
  }
  return pick;
}

/**
 * What a Portfolio game keeps from a contest's liquidity: the held positions and the other side
 * of each, so the cache holds a handful of positions instead of megabytes.
 */
export function trimForHoldings(positions: PositionLike[], heldHashes: Iterable<string>): { positions: PositionLike[] } {
  const want = new Set(heldHashes);
  const keep = new Set<PositionLike>();
  for (const p of positions) {
    if (!want.has(p.position_hash)) continue;
    keep.add(p);
    for (const c of complementCandidates(positions, p)) keep.add(c);
  }
  return { positions: positions.filter((p) => keep.has(p)) };
}

/**
 * The opposite-side stake that pays about the same if it wins: you hold `payout` contracts, so
 * buying `payout` contracts of the other side at `priceOpp` costs `payout × priceOpp`
 * (docs/portfolio-ux.md). Rounded to cents (whole coins for ATLAS).
 */
export function hedgeStake(payout: number, priceOpp: number, ccy: Ccy): number {
  if (!(payout > 0) || !(priceOpp > 0) || !(priceOpp < 1)) return 0;
  const raw = payout * priceOpp;
  return ccy === "ATLAS" ? Math.max(1, Math.round(raw)) : Math.round(raw * 100) / 100;
}

/* ------------------------------------------------------------- recently settled */

export const SETTLED_DAYS = 7;
const DAY_MS = 86_400_000;

/** The `from` date for the server-side settled filter (ISO day; the API takes days only). */
export function settledFromDate(now: number, days = SETTLED_DAYS): string {
  return new Date(now - days * DAY_MS).toISOString().slice(0, 10);
}

export type SettledResult = "win" | "lose" | "push";
const RESULTS = new Set<string>(["win", "lose", "push"]);

/**
 * Orders that settled to a result in the last `days` days, newest first. `cancelled` (never
 * took effect) isn't a result to show. The server filter is by day, so the exact window is here.
 */
export function recentSettled<O extends OrderLike>(orders: O[], now: number, days = SETTLED_DAYS): O[] {
  const since = now - days * DAY_MS;
  return orders
    .filter((o) => RESULTS.has(norm(o.result)) && tsOf(o.settled_datetime) >= since && tsOf(o.settled_datetime) <= now + 60_000)
    .sort((a, b) => tsOf(b.settled_datetime) - tsOf(a.settled_datetime));
}

/** Profit on a settled order — `net_winnings − amount`, as the execution docs define it. */
export function settledProfit(o: OrderLike): number {
  const amount = o.amount ?? 0;
  const r = norm(o.result);
  if (r === "lose") return -amount;
  if (r === "push") return (o.net_winnings ?? o.winnings ?? amount) - amount;
  return (o.net_winnings ?? o.winnings ?? 0) - amount;
}

export interface SettledRow {
  key: string;
  label: string;
  title: string;
  result: SettledResult | "mixed";
  /** Σ profit (win: + ; lose: − stake ; push: ≈ 0). */
  amount: number;
  stake: number;
  venueName: string;
  partnerId: string;
  orders: number;
  settledAt: number;
}

export interface SettledGame {
  contestId: string;
  rows: SettledRow[];
  settledAt: number;
}

/** Settled orders grouped by game, then side + venue; newest settlement first. */
export function groupSettled(orders: OrderLike[]): SettledGame[] {
  const games = new Map<string, SettledGame>();
  const rows = new Map<string, SettledRow>();
  for (const o of orders) {
    const r = norm(o.result) as SettledResult;
    if (!RESULTS.has(r)) continue;
    const contestId = o.contest_id ?? NO_CONTEST;
    const partnerId = o.partner_id ?? o.partner?.partner_id ?? "(venue)";
    const key = `${positionHashOf(o) ?? o.market_side_id ?? o.title ?? o.router_order_id}|${partnerId}`;
    const at = tsOf(o.settled_datetime);
    let g = games.get(contestId);
    if (!g) games.set(contestId, (g = { contestId, rows: [], settledAt: 0 }));
    g.settledAt = Math.max(g.settledAt, at);
    let row = rows.get(`${contestId}::${key}`);
    if (!row) {
      row = {
        key,
        label: shortOutcome(o.title),
        title: o.title ?? "—",
        result: r,
        amount: 0,
        stake: 0,
        venueName: o.partner?.partner_name ?? partnerId,
        partnerId,
        orders: 0,
        settledAt: 0,
      };
      rows.set(`${contestId}::${key}`, row);
      g.rows.push(row);
    } else if (row.result !== r) {
      row.result = "mixed";
    }
    row.amount += settledProfit(o);
    row.stake += o.amount ?? 0;
    row.orders += 1;
    row.settledAt = Math.max(row.settledAt, at);
  }
  return [...games.values()].sort((a, b) => b.settledAt - a.settledAt || a.contestId.localeCompare(b.contestId));
}

/* -------------------------------------------------------------------- working */

/** Fill progress of a resting order, 0–100. */
export function fillPct(o: { amount?: number | null; filled_amt?: number | null; unfilled_amt?: number | null }): number {
  const filled = o.filled_amt ?? 0;
  const total = o.amount ?? filled + (o.unfilled_amt ?? 0);
  return total > 0 ? Math.min(100, Math.max(0, (filled / total) * 100)) : 0;
}

/** Still waiting to fill (the unfilled remainder). */
export function waitingAmt(o: { amount?: number | null; filled_amt?: number | null; unfilled_amt?: number | null }): number {
  if (o.unfilled_amt != null) return Math.max(0, o.unfilled_amt);
  return Math.max(0, (o.amount ?? 0) - (o.filled_amt ?? 0));
}

/**
 * How long a resting order has waited, in seconds: from `placed_at` when present, else the
 * server's `seconds_since_placed` advanced by the time since that response was fetched.
 */
export function waitedSeconds(
  o: { placed_at?: string | null; seconds_since_placed?: number | null },
  now: number,
  fetchedAt: number,
): number | null {
  const placed = tsOf(o.placed_at);
  if (placed > 0) return Math.max(0, (now - placed) / 1000);
  if (o.seconds_since_placed != null) return Math.max(0, o.seconds_since_placed + (now - fetchedAt) / 1000);
  return null;
}

/** "45s", "12m", "3h", "2d". */
export function fmtWaited(sec: number | null): string {
  if (sec == null || !Number.isFinite(sec)) return "—";
  if (sec < 60) return `${Math.floor(sec)}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  if (sec < 86_400) return `${Math.floor(sec / 3600)}h`;
  return `${Math.floor(sec / 86_400)}d`;
}

/* ------------------------------------------------------------------- presence */

/** An item on screen, entering, or on its way out (kept briefly so it can animate away). */
export interface PresenceEntry<T> {
  key: string;
  item: T;
  phase: "enter" | "stay" | "exit";
}

/**
 * Reconcile what's on screen with the next list: new items enter, vanished ones exit (kept in
 * their old slot), returning ones come back. With `animate` off everything is simply `stay`.
 */
export function reconcilePresence<T>(
  prev: PresenceEntry<T>[],
  next: T[],
  keyOf: (t: T) => string,
  animate: boolean,
): PresenceEntry<T>[] {
  const prevByKey = new Map(prev.map((e) => [e.key, e]));
  const out: PresenceEntry<T>[] = next.map((item) => {
    const key = keyOf(item);
    const was = prevByKey.get(key);
    const phase: PresenceEntry<T>["phase"] = !animate ? "stay" : !was || was.phase === "exit" ? "enter" : was.phase;
    return { key, item, phase };
  });
  if (!animate) return out;
  const nextKeys = new Set(out.map((e) => e.key));
  prev.forEach((e, i) => {
    if (nextKeys.has(e.key)) return;
    out.splice(Math.min(i, out.length), 0, { ...e, phase: "exit" });
  });
  return out;
}

/** After the animation: exits are gone, enters settle. */
export function settlePresence<T>(entries: PresenceEntry<T>[]): PresenceEntry<T>[] {
  return entries.filter((e) => e.phase !== "exit").map((e) => (e.phase === "enter" ? { ...e, phase: "stay" } : e));
}
